import * as THREE from "three";
import {
  createRotationGizmo,
  disposeRotationGizmo,
  getRotationAxisWorldDirection,
  setDraggingRotationAxis,
  setHoveredRotationAxis,
  updateRotationGizmoTransform,
  type RotationAxisIndex,
  type RotationGizmo,
} from "./rotationGizmoObject";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { getHistoryActions } from "../stores/historyStore";
import { getSavedCameraWorldPose } from "../utils/savedCameraWorldPose";
import { logger } from "../utils/logger";
import { commitBodyTransform } from "../viewer/utils/commitTransform";
import type { BodyUUID, CameraUUID, SiteUUID } from "../types/primitives";
import {
  buildParentMapFromSceneState,
  filterTopmostBodies,
  captureMultiBodySnapshots,
  computeSnapshotCentroid,
  type MultiBodySnapshot,
} from "./multiBodyTransformUtils";

type MaybeRef<T> = { current: T | null | undefined } | null | undefined;
type GeomGizmoTarget = {
  bodyUuid: string;
  geomId: string;
  object?: THREE.Object3D | null;
};
type MultiGeomRotationSnapshot = GeomGizmoTarget & {
  startPos: [number, number, number];
  startQuat: [number, number, number, number];
};
type SceneGeomEntry = { uuid?: string; position?: unknown; orientation?: unknown };
type SceneBodyLookup = Record<
  string,
  { geoms?: SceneGeomEntry[]; inertia?: { position?: unknown; orientation?: unknown } } | undefined
>;
type SceneSiteLookup = Record<string, { position?: unknown; orientation?: unknown } | undefined>;

export type AttachRotationGizmoOptions = {
  sceneRef: MaybeRef<THREE.Scene>;
  cameraRef: MaybeRef<THREE.Camera>;
  rendererRef: MaybeRef<THREE.WebGLRenderer>;
  controlsRef: MaybeRef<{ enabled: boolean }>;
  sceneObjectsRef: MaybeRef<Record<string, THREE.Object3D>>;
  getSelectedBodyUuid: () => string | null;
  getSelectedTarget?: () =>
    | { kind: "body"; bodyUuid: string }
    | { kind: "geom"; bodyUuid: string; geomId: string; object?: THREE.Object3D | null }
    | { kind: "site"; bodyUuid: string; siteUuid: string; object?: THREE.Object3D | null }
    | { kind: "inertial"; bodyUuid: string; object?: THREE.Object3D | null }
    | { kind: "camera"; cameraUuid: string }
    | null;
  /** Returns all currently selected body UUIDs; length > 1 activates multi-body mode. */
  getSelectedBodies?: () => string[];
  /** Returns same-body visual geom targets for multi-mesh transform mode. */
  getSelectedGeomTargets?: () => GeomGizmoTarget[];
  isDisabled: () => boolean;
  isOrbiting: () => boolean;
};

export type RotationGizmoController = {
  frameUpdate: () => void;
  detach: () => void;
};

const AXIS_LETTERS: ["X", "Y", "Z"] = ["X", "Y", "Z"];

function computeBodyWorldMatrix(bodyObj: THREE.Object3D): THREE.Matrix4 {
  const chain: THREE.Object3D[] = [bodyObj];
  const seen = new Set<THREE.Object3D>([bodyObj]);
  let cur: THREE.Object3D | null = bodyObj.parent || null;
  while (cur) {
    if (seen.has(cur)) break;
    seen.add(cur);
    chain.push(cur);
    cur = cur.parent;
  }
  const world = new THREE.Matrix4().identity();
  for (let i = chain.length - 1; i >= 0; i--) {
    const n = chain[i];
    if (typeof n.updateMatrix === "function") n.updateMatrix();
    if (n.matrix) world.multiply(n.matrix);
  }
  return world;
}

function computeParentWorldMatrix(bodyObj: THREE.Object3D): THREE.Matrix4 {
  const chain: THREE.Object3D[] = [];
  const seen = new Set<THREE.Object3D>();
  let cur: THREE.Object3D | null = bodyObj.parent || null;
  while (cur) {
    if (seen.has(cur)) break;
    seen.add(cur);
    chain.push(cur);
    cur = cur.parent;
  }
  const world = new THREE.Matrix4().identity();
  for (let i = chain.length - 1; i >= 0; i--) {
    const n = chain[i];
    if (typeof n.updateMatrix === "function") n.updateMatrix();
    if (n.matrix) world.multiply(n.matrix);
  }
  return world;
}

function captureMultiGeomRotationSnapshots(
  targets: GeomGizmoTarget[]
): MultiGeomRotationSnapshot[] {
  const sceneState = getSceneState();
  const seen = new Set<string>();
  const bodyUuids = new Set<string>();
  const snapshots: MultiGeomRotationSnapshot[] = [];

  for (const target of targets || []) {
    if (!target?.bodyUuid || !target?.geomId) continue;
    const key = `${target.bodyUuid}:${target.geomId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const body = (sceneState.bodies as SceneBodyLookup | undefined)?.[target.bodyUuid];
    const geom = Array.isArray(body?.geoms)
      ? body.geoms.find((entry) => String(entry?.uuid) === String(target.geomId))
      : null;
    if (!geom) continue;
    const pos = Array.isArray(geom.position) ? geom.position : [0, 0, 0];
    const quat = Array.isArray(geom.orientation) ? geom.orientation : [1, 0, 0, 0];
    bodyUuids.add(target.bodyUuid);
    snapshots.push({
      ...target,
      startPos: [Number(pos[0] ?? 0), Number(pos[1] ?? 0), Number(pos[2] ?? 0)],
      startQuat: [
        Number(quat[0] ?? 1),
        Number(quat[1] ?? 0),
        Number(quat[2] ?? 0),
        Number(quat[3] ?? 0),
      ],
    });
  }

  return bodyUuids.size === 1 && snapshots.length > 1 ? snapshots : [];
}

function computeMultiGeomLocalCentroid(snapshots: MultiGeomRotationSnapshot[]): THREE.Vector3 {
  const centroid = new THREE.Vector3();
  snapshots.forEach((snapshot) => {
    centroid.add(
      new THREE.Vector3(snapshot.startPos[0], snapshot.startPos[1], snapshot.startPos[2])
    );
  });
  return snapshots.length > 0 ? centroid.divideScalar(snapshots.length) : centroid;
}

function setNdcFromEvent(
  pointer: THREE.Vector2,
  event: PointerEvent,
  rendererDom: HTMLElement
): void {
  const rect = rendererDom.getBoundingClientRect();
  pointer.set(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -(((event.clientY - rect.top) / rect.height) * 2 - 1)
  );
}

function intersectRayWithPlane(
  ray: THREE.Ray,
  planeOrigin: THREE.Vector3,
  planeNormal: THREE.Vector3,
  out: THREE.Vector3
): boolean {
  const denom = planeNormal.dot(ray.direction);
  if (Math.abs(denom) < 1e-7) return false;
  const t = planeNormal.dot(out.copy(planeOrigin).sub(ray.origin)) / denom;
  if (!Number.isFinite(t)) return false;
  out.copy(ray.direction).multiplyScalar(t).add(ray.origin);
  return true;
}

function quatFromWxyz(arr: [number, number, number, number]): THREE.Quaternion {
  return new THREE.Quaternion(arr[1], arr[2], arr[3], arr[0]);
}

function quatToWxyz(q: THREE.Quaternion): [number, number, number, number] {
  return [q.w, q.x, q.y, q.z];
}

export function attachRotationGizmoController(
  opts: AttachRotationGizmoOptions
): RotationGizmoController {
  const {
    sceneRef,
    cameraRef,
    rendererRef,
    controlsRef,
    sceneObjectsRef,
    getSelectedBodyUuid,
    isDisabled,
    isOrbiting,
  } = opts;

  const scene = sceneRef?.current;
  const renderer = rendererRef?.current;
  const camera = cameraRef?.current;
  if (!scene || !renderer || !camera) {
    return { frameUpdate: () => {}, detach: () => {} };
  }

  const gizmo: RotationGizmo = createRotationGizmo();
  scene.add(gizmo.root);

  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const rendererDom = renderer.domElement;

  let hoveredAxis: RotationAxisIndex | null = null;
  let dragState: {
    axis: RotationAxisIndex;
    target:
      | { kind: "body"; bodyUuid: string }
      | { kind: "geom"; bodyUuid: string; geomId: string; object?: THREE.Object3D | null }
      | { kind: "site"; bodyUuid: string; siteUuid: string; object?: THREE.Object3D | null }
      | { kind: "inertial"; bodyUuid: string; object?: THREE.Object3D | null }
      | { kind: "camera"; cameraUuid: string };
    startPos: [number, number, number];
    startQuat: [number, number, number, number];
    axisDirWorld: THREE.Vector3;
    originWorld: THREE.Vector3;
    startDirOnPlane: THREE.Vector3;
    startedHistoryGroup: boolean;
    pointerId: number | null;
    /** Set when rotating multiple bodies simultaneously; centroid is the pivot point. */
    multiBodyTargets?: MultiBodySnapshot[];
    multiGeomTargets?: MultiGeomRotationSnapshot[];
    centroid?: THREE.Vector3;
  } | null = null;

  const resolveSceneObjectForTarget = (
    target:
      | { kind: "body"; bodyUuid: string }
      | { kind: "geom"; bodyUuid: string; geomId: string; object?: THREE.Object3D | null }
      | { kind: "site"; bodyUuid: string; siteUuid: string; object?: THREE.Object3D | null }
      | { kind: "inertial"; bodyUuid: string; object?: THREE.Object3D | null }
      | null
  ): THREE.Object3D | null => {
    if (!target) return null;
    if (target.kind !== "body") return target.object || null;
    return sceneObjectsRef?.current?.[target.bodyUuid] || null;
  };

  const resolveOrientationObjectForTarget = (
    target:
      | { kind: "body"; bodyUuid: string }
      | { kind: "geom"; bodyUuid: string; geomId: string; object?: THREE.Object3D | null }
      | { kind: "site"; bodyUuid: string; siteUuid: string; object?: THREE.Object3D | null }
      | { kind: "inertial"; bodyUuid: string; object?: THREE.Object3D | null }
  ): THREE.Object3D | null => {
    if (target.kind !== "geom") return target.kind === "body" ? null : target.object || null;
    const root = target.object || null;
    if (!root) return null;
    let geomLayer: THREE.Object3D | null = null;
    root.traverse((child) => {
      if (!geomLayer && child.userData?.transformLayer === "geom") geomLayer = child;
    });
    return geomLayer || root;
  };

  const dispatchDragEvent = (
    type: "rotation-gizmo-drag" | "rotation-gizmo-drag-end",
    detail: Record<string, unknown> | null
  ) => {
    try {
      window.dispatchEvent(new CustomEvent(type, { detail: detail || {} }));
    } catch {
      // ignore
    }
  };

  const updateHover = (event: PointerEvent): RotationAxisIndex | null => {
    if (!gizmo.root.visible) return null;
    setNdcFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(gizmo.pickTargets, false);
    if (!hits.length) return null;
    const axis = hits[0].object.userData?.rotationGizmoAxis;
    return typeof axis === "number" ? (axis as RotationAxisIndex) : null;
  };

  const setHover = (axis: RotationAxisIndex | null) => {
    if (hoveredAxis === axis) return;
    hoveredAxis = axis;
    setHoveredRotationAxis(gizmo, axis);
    if (!dragState) rendererDom.style.cursor = axis == null ? "" : "grab";
  };

  const onPointerMoveCanvas = (event: PointerEvent) => {
    if (dragState || isOrbiting() || isDisabled() || !gizmo.root.visible) {
      if (!dragState) setHover(null);
      return;
    }
    setHover(updateHover(event));
  };

  const onPointerLeaveCanvas = () => {
    if (!dragState) setHover(null);
  };

  const onPointerDownWindow = (event: PointerEvent) => {
    if (event.button !== 0) return;
    const eventTarget = event.target as Node | null;
    if (!eventTarget || (eventTarget !== rendererDom && !rendererDom.contains(eventTarget))) return;
    if (isDisabled() || !gizmo.root.visible) return;

    const axis = updateHover(event);
    if (axis == null) return;
    const explicitTarget = opts.getSelectedTarget?.();

    const multiGeomTargets = captureMultiGeomRotationSnapshots(
      opts.getSelectedGeomTargets?.() ?? []
    );
    if (multiGeomTargets.length > 1 && (!explicitTarget || explicitTarget.kind === "geom")) {
      const originWorld = gizmo.root.getWorldPosition(new THREE.Vector3());
      const axisDirWorld = getRotationAxisWorldDirection(gizmo, axis, new THREE.Vector3());

      setNdcFromEvent(pointer, event, rendererDom);
      raycaster.setFromCamera(pointer, camera);
      const startPoint = new THREE.Vector3();
      if (!intersectRayWithPlane(raycaster.ray, originWorld, axisDirWorld, startPoint)) return;
      const startDir = startPoint.clone().sub(originWorld);
      if (startDir.lengthSq() < 1e-10) return;
      startDir.normalize();

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      dragState = {
        axis,
        target: {
          kind: "geom" as const,
          bodyUuid: multiGeomTargets[0].bodyUuid,
          geomId: multiGeomTargets[0].geomId,
          object: multiGeomTargets[0].object,
        },
        startPos: multiGeomTargets[0].startPos,
        startQuat: multiGeomTargets[0].startQuat,
        axisDirWorld,
        originWorld: originWorld.clone(),
        startDirOnPlane: startDir.clone(),
        startedHistoryGroup: false,
        pointerId: event.pointerId,
        multiGeomTargets,
        centroid: computeMultiGeomLocalCentroid(multiGeomTargets),
      };
      setDraggingRotationAxis(gizmo, axis);
      try {
        getHistoryActions().beginGroup(getSceneState());
        dragState.startedHistoryGroup = true;
      } catch (error) {
        logger.warn("[RotationGizmo] Failed to begin history group", error);
      }
      if (controlsRef?.current) controlsRef.current.enabled = false;
      rendererDom.style.cursor = "grabbing";
      try {
        rendererDom.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      window.addEventListener("pointermove", onPointerMoveWindow, true);
      window.addEventListener("pointerup", onPointerUpWindow, true);
      window.addEventListener("pointercancel", onPointerUpWindow, true);
      return;
    }

    // Multi-body drag: intercept when multiple bodies are selected and no specific sub-target.
    const selectedBodies = opts.getSelectedBodies?.() ?? [];
    if (selectedBodies.length > 1 && (!explicitTarget || explicitTarget.kind === "body")) {
      const sceneObjs = sceneObjectsRef?.current;
      if (!sceneObjs) return;
      const parentMap = buildParentMapFromSceneState();
      const topmost = filterTopmostBodies(selectedBodies, parentMap);
      const snapshots = captureMultiBodySnapshots(topmost, sceneObjs);
      if (!snapshots.length) return;
      const centroid = computeSnapshotCentroid(snapshots);

      const originWorld = gizmo.root.getWorldPosition(new THREE.Vector3());
      const axisDirWorld = getRotationAxisWorldDirection(gizmo, axis, new THREE.Vector3());

      setNdcFromEvent(pointer, event, rendererDom);
      raycaster.setFromCamera(pointer, camera);
      const startPoint = new THREE.Vector3();
      if (!intersectRayWithPlane(raycaster.ray, originWorld, axisDirWorld, startPoint)) return;
      const startDir = startPoint.clone().sub(originWorld);
      if (startDir.lengthSq() < 1e-10) return;
      startDir.normalize();

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      dragState = {
        axis,
        target: { kind: "body" as const, bodyUuid: topmost[0] },
        startPos: snapshots[0].startLocalPos,
        startQuat: snapshots[0].startQuat,
        axisDirWorld,
        originWorld: originWorld.clone(),
        startDirOnPlane: startDir.clone(),
        startedHistoryGroup: false,
        pointerId: event.pointerId,
        multiBodyTargets: snapshots,
        centroid,
      };
      setDraggingRotationAxis(gizmo, axis);
      try {
        getHistoryActions().beginGroup(getSceneState());
        dragState.startedHistoryGroup = true;
      } catch (error) {
        logger.warn("[RotationGizmo] Failed to begin history group", error);
      }
      if (controlsRef?.current) controlsRef.current.enabled = false;
      rendererDom.style.cursor = "grabbing";
      try {
        rendererDom.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      window.addEventListener("pointermove", onPointerMoveWindow, true);
      window.addEventListener("pointerup", onPointerUpWindow, true);
      window.addEventListener("pointercancel", onPointerUpWindow, true);
      return;
    }

    const fallbackBodyUuid = getSelectedBodyUuid();
    const target = explicitTarget
      ? explicitTarget
      : fallbackBodyUuid
        ? { kind: "body" as const, bodyUuid: fallbackBodyUuid }
        : null;
    if (!target) return;
    if (target.kind !== "camera") {
      const sceneObj = resolveSceneObjectForTarget(target);
      if (!sceneObj) return;
    }

    const actions = getSceneActions();
    let startPos: [number, number, number] = [0, 0, 0];
    let startQuat: [number, number, number, number] = [1, 0, 0, 0];
    if (target.kind === "body") {
      const bodyUuidBranded = target.bodyUuid as BodyUUID;
      startPos = (actions.getBodyPosition(bodyUuidBranded) || [0, 0, 0]) as [
        number,
        number,
        number,
      ];
      startQuat = (actions.getBodyRotation(bodyUuidBranded) || [1, 0, 0, 0]) as [
        number,
        number,
        number,
        number,
      ];
    } else if (target.kind === "camera") {
      const camStore = (
        getSceneState().cameras as Record<string, { orientation?: number[] } | undefined>
      )?.[target.cameraUuid];
      const quatArr = Array.isArray(camStore?.orientation) ? camStore.orientation : [1, 0, 0, 0];
      startPos = [0, 0, 0];
      startQuat = [
        Number(quatArr[0] ?? 1),
        Number(quatArr[1] ?? 0),
        Number(quatArr[2] ?? 0),
        Number(quatArr[3] ?? 0),
      ];
    } else if (target.kind === "geom") {
      const body = (getSceneState().bodies as SceneBodyLookup | undefined)?.[target.bodyUuid];
      const geom = Array.isArray(body?.geoms)
        ? body.geoms.find((entry) => String(entry?.uuid) === String(target.geomId))
        : null;
      const pos = Array.isArray(geom?.position) ? geom.position : [0, 0, 0];
      const quat = Array.isArray(geom?.orientation) ? geom.orientation : [1, 0, 0, 0];
      startPos = [Number(pos[0] ?? 0), Number(pos[1] ?? 0), Number(pos[2] ?? 0)];
      startQuat = [
        Number(quat[0] ?? 1),
        Number(quat[1] ?? 0),
        Number(quat[2] ?? 0),
        Number(quat[3] ?? 0),
      ];
    } else if (target.kind === "site") {
      const site = (getSceneState().sites as SceneSiteLookup | undefined)?.[target.siteUuid];
      const pos = Array.isArray(site?.position) ? site.position : [0, 0, 0];
      const quat = Array.isArray(site?.orientation) ? site.orientation : [1, 0, 0, 0];
      startPos = [Number(pos[0] ?? 0), Number(pos[1] ?? 0), Number(pos[2] ?? 0)];
      startQuat = [
        Number(quat[0] ?? 1),
        Number(quat[1] ?? 0),
        Number(quat[2] ?? 0),
        Number(quat[3] ?? 0),
      ];
    } else if (target.kind === "inertial") {
      const inertial = (getSceneState().bodies as SceneBodyLookup | undefined)?.[target.bodyUuid]
        ?.inertia;
      const pos = Array.isArray(inertial?.position) ? inertial.position : [0, 0, 0];
      const quat = Array.isArray(inertial?.orientation) ? inertial.orientation : [1, 0, 0, 0];
      startPos = [Number(pos[0] ?? 0), Number(pos[1] ?? 0), Number(pos[2] ?? 0)];
      startQuat = [
        Number(quat[0] ?? 1),
        Number(quat[1] ?? 0),
        Number(quat[2] ?? 0),
        Number(quat[3] ?? 0),
      ];
    }

    const originWorld = gizmo.root.getWorldPosition(new THREE.Vector3());
    const axisDirWorld = getRotationAxisWorldDirection(gizmo, axis, new THREE.Vector3());

    setNdcFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const startPoint = new THREE.Vector3();
    if (!intersectRayWithPlane(raycaster.ray, originWorld, axisDirWorld, startPoint)) return;
    const startDir = startPoint.sub(originWorld);
    if (startDir.lengthSq() < 1e-10) return;
    startDir.normalize();

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    dragState = {
      axis,
      target,
      startPos: [startPos[0], startPos[1], startPos[2]],
      startQuat: [startQuat[0], startQuat[1], startQuat[2], startQuat[3]],
      axisDirWorld,
      originWorld: originWorld.clone(),
      startDirOnPlane: startDir.clone(),
      startedHistoryGroup: false,
      pointerId: event.pointerId,
    };
    setDraggingRotationAxis(gizmo, axis);
    try {
      getHistoryActions().beginGroup(getSceneState());
      dragState.startedHistoryGroup = true;
    } catch (error) {
      logger.warn("[RotationGizmo] Failed to begin history group", error);
    }

    if (controlsRef?.current) controlsRef.current.enabled = false;
    rendererDom.style.cursor = "grabbing";
    try {
      rendererDom.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }

    window.addEventListener("pointermove", onPointerMoveWindow, true);
    window.addEventListener("pointerup", onPointerUpWindow, true);
    window.addEventListener("pointercancel", onPointerUpWindow, true);
  };

  const onPointerMoveWindow = (event: PointerEvent) => {
    if (!dragState) return;
    setNdcFromEvent(pointer, event, rendererDom);
    raycaster.setFromCamera(pointer, camera);
    const point = new THREE.Vector3();
    if (!intersectRayWithPlane(raycaster.ray, dragState.originWorld, dragState.axisDirWorld, point))
      return;
    const curDir = point.sub(dragState.originWorld);
    if (curDir.lengthSq() < 1e-10) return;
    curDir.normalize();

    const cross = new THREE.Vector3().crossVectors(dragState.startDirOnPlane, curDir);
    const dot = THREE.MathUtils.clamp(dragState.startDirOnPlane.dot(curDir), -1, 1);
    const signed = Math.atan2(dragState.axisDirWorld.dot(cross), dot);
    if (!Number.isFinite(signed)) return;

    // Multi-body rotation: rotate all bodies around the centroid pivot.
    if (dragState.multiBodyTargets && dragState.multiBodyTargets.length > 0 && dragState.centroid) {
      const deltaWorldQuat = new THREE.Quaternion().setFromAxisAngle(
        dragState.axisDirWorld,
        signed
      );
      const centroid = dragState.centroid;
      for (const mb of dragState.multiBodyTargets) {
        const parentWorldInvQuat = mb.parentWorldQuat.clone().invert();

        // Rotate body's world position around the centroid.
        const offsetFromCentroid = mb.bodyWorldPos.clone().sub(centroid);
        const rotatedOffset = offsetFromCentroid.clone().applyQuaternion(deltaWorldQuat);
        const newWorldPos = centroid.clone().add(rotatedOffset);

        // Convert new world pos to parent-local space.
        const newParentLocalVec = newWorldPos
          .clone()
          .sub(mb.parentWorldOrigin)
          .applyQuaternion(parentWorldInvQuat);
        const newLocalPos: [number, number, number] = [
          newParentLocalVec.x,
          newParentLocalVec.y,
          newParentLocalVec.z,
        ];

        // Rotate body's orientation in world space, then convert to parent-local.
        const bodyStartWorldQuat = mb.parentWorldQuat.clone().multiply(quatFromWxyz(mb.startQuat));
        const newBodyWorldQuat = deltaWorldQuat.clone().multiply(bodyStartWorldQuat);
        const newBodyLocalQuat = parentWorldInvQuat.clone().multiply(newBodyWorldQuat).normalize();
        const nextQuat = quatToWxyz(newBodyLocalQuat);

        try {
          commitBodyTransform(getSceneActions(), mb.bodyUuid, newLocalPos, nextQuat);
        } catch (error) {
          logger.warn("[RotationGizmo] Failed to commit multi-body transform", error);
        }
      }
      return;
    }

    if (dragState.multiGeomTargets && dragState.multiGeomTargets.length > 0 && dragState.centroid) {
      const localAxis = new THREE.Vector3(
        dragState.axis === 0 ? 1 : 0,
        dragState.axis === 1 ? 1 : 0,
        dragState.axis === 2 ? 1 : 0
      );
      const deltaLocal = new THREE.Quaternion().setFromAxisAngle(localAxis, signed);
      for (const geomTarget of dragState.multiGeomTargets) {
        const startOffset = new THREE.Vector3(
          geomTarget.startPos[0],
          geomTarget.startPos[1],
          geomTarget.startPos[2]
        ).sub(dragState.centroid);
        const nextPosVec = dragState.centroid.clone().add(startOffset.applyQuaternion(deltaLocal));
        const nextPos: [number, number, number] = [nextPosVec.x, nextPosVec.y, nextPosVec.z];
        const nextQuat = quatToWxyz(
          deltaLocal.clone().multiply(quatFromWxyz(geomTarget.startQuat)).normalize()
        );
        try {
          getSceneActions().setGeomPosition(
            geomTarget.bodyUuid as BodyUUID,
            geomTarget.geomId,
            nextPos
          );
          getSceneActions().setGeomOrientation(
            geomTarget.bodyUuid as BodyUUID,
            geomTarget.geomId,
            nextQuat
          );
          geomTarget.object?.position?.set(nextPos[0], nextPos[1], nextPos[2]);
          geomTarget.object?.quaternion?.set(nextQuat[1], nextQuat[2], nextQuat[3], nextQuat[0]);
        } catch (error) {
          logger.warn("[RotationGizmo] Failed to commit multi-geom transform", error);
        }
      }
      return;
    }

    const localAxis = new THREE.Vector3(
      dragState.axis === 0 ? 1 : 0,
      dragState.axis === 1 ? 1 : 0,
      dragState.axis === 2 ? 1 : 0
    );
    const deltaLocal = new THREE.Quaternion().setFromAxisAngle(localAxis, signed);
    const startLocal = quatFromWxyz(dragState.startQuat);
    const nextLocal = new THREE.Quaternion().copy(deltaLocal).multiply(startLocal).normalize();
    const nextQuat = quatToWxyz(nextLocal);

    if (dragState.target.kind === "body") {
      commitBodyTransform(
        getSceneActions(),
        dragState.target.bodyUuid,
        dragState.startPos,
        nextQuat
      );
    } else if (dragState.target.kind === "camera") {
      getSceneActions().updateCamera(dragState.target.cameraUuid as CameraUUID, {
        orientation: nextQuat,
      });
    } else if (dragState.target.kind === "geom") {
      getSceneActions().setGeomOrientation(
        dragState.target.bodyUuid as BodyUUID,
        dragState.target.geomId,
        nextQuat
      );
      resolveOrientationObjectForTarget(dragState.target)?.quaternion?.set(
        nextQuat[1],
        nextQuat[2],
        nextQuat[3],
        nextQuat[0]
      );
    } else if (dragState.target.kind === "site") {
      getSceneActions().setSiteOrientation(dragState.target.siteUuid as SiteUUID, nextQuat);
      resolveOrientationObjectForTarget(dragState.target)?.quaternion?.set(
        nextQuat[1],
        nextQuat[2],
        nextQuat[3],
        nextQuat[0]
      );
    } else if (dragState.target.kind === "inertial") {
      getSceneActions().updateInertia(dragState.target.bodyUuid as BodyUUID, {
        orientation: nextQuat,
      });
      resolveOrientationObjectForTarget(dragState.target)?.quaternion?.set(
        nextQuat[1],
        nextQuat[2],
        nextQuat[3],
        nextQuat[0]
      );
    }
    dispatchDragEvent("rotation-gizmo-drag", {
      axis: dragState.axis,
      axisLetter: AXIS_LETTERS[dragState.axis],
      degrees: THREE.MathUtils.radToDeg(signed),
      clientX: event.clientX,
      clientY: event.clientY,
      bodyUuid: dragState.target.kind === "camera" ? undefined : dragState.target.bodyUuid,
      cameraUuid: dragState.target.kind === "camera" ? dragState.target.cameraUuid : undefined,
    });
  };

  const onPointerUpWindow = (event: PointerEvent) => {
    if (!dragState) return;
    const endedGroup = dragState.startedHistoryGroup;
    const pointerId = dragState.pointerId;
    const axis = dragState.axis;
    const bodyUuid = dragState.target.kind === "camera" ? undefined : dragState.target.bodyUuid;
    const cameraUuid = dragState.target.kind === "camera" ? dragState.target.cameraUuid : undefined;
    dragState = null;
    setDraggingRotationAxis(gizmo, null);

    window.removeEventListener("pointermove", onPointerMoveWindow, true);
    window.removeEventListener("pointerup", onPointerUpWindow, true);
    window.removeEventListener("pointercancel", onPointerUpWindow, true);
    if (controlsRef?.current) controlsRef.current.enabled = true;
    rendererDom.style.cursor = hoveredAxis == null ? "" : "grab";
    if (pointerId != null) {
      try {
        rendererDom.releasePointerCapture(pointerId);
      } catch {
        // ignore
      }
    }
    if (endedGroup) getHistoryActions().endGroup(getSceneState());
    dispatchDragEvent("rotation-gizmo-drag-end", {
      axis,
      axisLetter: AXIS_LETTERS[axis],
      bodyUuid,
      cameraUuid,
      clientX: event?.clientX,
      clientY: event?.clientY,
    });
  };

  window.addEventListener("pointerdown", onPointerDownWindow, true);
  rendererDom.addEventListener("pointermove", onPointerMoveCanvas);
  rendererDom.addEventListener("pointerleave", onPointerLeaveCanvas);

  const frameUpdate = () => {
    if (isDisabled()) {
      if (gizmo.root.visible) {
        gizmo.root.visible = false;
        setDraggingRotationAxis(gizmo, null);
        setHover(null);
      }
      return;
    }

    // Multi-body mode: place gizmo at centroid of all selected bodies.
    const multiGeomTargets = captureMultiGeomRotationSnapshots(
      opts.getSelectedGeomTargets?.() ?? []
    );
    if (multiGeomTargets.length > 1) {
      const sceneObjs = sceneObjectsRef?.current;
      const bodyObj = sceneObjs?.[multiGeomTargets[0].bodyUuid];
      if (!bodyObj) {
        if (gizmo.root.visible) {
          gizmo.root.visible = false;
          setDraggingRotationAxis(gizmo, null);
          setHover(null);
        }
        return;
      }
      const bodyWorldMat = computeBodyWorldMatrix(bodyObj);
      const bodyQuat = new THREE.Quaternion().setFromRotationMatrix(bodyWorldMat);
      const centroid = computeMultiGeomLocalCentroid(multiGeomTargets).applyMatrix4(bodyWorldMat);
      const camPos = new THREE.Vector3();
      camera.getWorldPosition(camPos);
      const distance = camPos.distanceTo(centroid);
      updateRotationGizmoTransform(gizmo, centroid, bodyQuat, distance);
      if (!gizmo.root.visible) gizmo.root.visible = true;
      return;
    }

    // Multi-body mode: place gizmo at centroid of all selected bodies.
    const selectedBodies = opts.getSelectedBodies?.() ?? [];
    if (selectedBodies.length > 1) {
      const explicitTarget = opts.getSelectedTarget?.();
      if (!explicitTarget || explicitTarget.kind === "body") {
        const sceneObjs = sceneObjectsRef?.current;
        if (!sceneObjs) {
          if (gizmo.root.visible) {
            gizmo.root.visible = false;
            setDraggingRotationAxis(gizmo, null);
            setHover(null);
          }
          return;
        }
        const centroid = new THREE.Vector3();
        let count = 0;
        for (const uuid of selectedBodies) {
          const obj = sceneObjs[uuid];
          if (!obj) continue;
          const wm = computeBodyWorldMatrix(obj);
          centroid.add(new THREE.Vector3().setFromMatrixPosition(wm));
          count++;
        }
        if (count === 0) {
          if (gizmo.root.visible) {
            gizmo.root.visible = false;
            setDraggingRotationAxis(gizmo, null);
            setHover(null);
          }
          return;
        }
        centroid.divideScalar(count);
        const parentQuat = new THREE.Quaternion();
        const firstObj = sceneObjs[selectedBodies[0]];
        if (firstObj) {
          const parentMat = computeParentWorldMatrix(firstObj);
          parentQuat.setFromRotationMatrix(parentMat);
        }
        const camPos = new THREE.Vector3();
        camera.getWorldPosition(camPos);
        const distance = camPos.distanceTo(centroid);
        updateRotationGizmoTransform(gizmo, centroid, parentQuat, distance);
        if (!gizmo.root.visible) gizmo.root.visible = true;
        return;
      }
    }

    const explicitTarget = opts.getSelectedTarget?.();
    const fallbackBodyUuid = getSelectedBodyUuid();
    const target = explicitTarget
      ? explicitTarget
      : fallbackBodyUuid
        ? { kind: "body" as const, bodyUuid: fallbackBodyUuid }
        : null;
    if (!target) {
      if (gizmo.root.visible) {
        gizmo.root.visible = false;
        setDraggingRotationAxis(gizmo, null);
        setHover(null);
      }
      return;
    }

    if (target.kind === "camera") {
      const sceneState = getSceneState();
      const camDef = (
        sceneState.cameras as unknown as Record<string, Record<string, unknown> | undefined>
      )?.[target.cameraUuid];
      if (!camDef) {
        if (gizmo.root.visible) {
          gizmo.root.visible = false;
          setDraggingRotationAxis(gizmo, null);
          setHover(null);
        }
        return;
      }
      const modelGroup = scene.getObjectByName("modelGroup") || null;
      const pose = getSavedCameraWorldPose(
        camDef,
        sceneObjectsRef?.current ?? {},
        sceneState.bodies ?? {},
        sceneState.sites ?? {},
        modelGroup
      );
      const origin = pose.position.clone();
      const worldCamQuat = pose.quaternion.clone();
      const camPos = new THREE.Vector3();
      camera.getWorldPosition(camPos);
      const distance = camPos.distanceTo(origin);
      updateRotationGizmoTransform(gizmo, origin, worldCamQuat, distance);
      if (!gizmo.root.visible) gizmo.root.visible = true;
      return;
    }

    const sceneObj = resolveSceneObjectForTarget(target);
    if (!sceneObj) {
      if (gizmo.root.visible) {
        gizmo.root.visible = false;
        setDraggingRotationAxis(gizmo, null);
        setHover(null);
      }
      return;
    }

    const targetWorld = computeBodyWorldMatrix(sceneObj);
    const origin = new THREE.Vector3().setFromMatrixPosition(targetWorld);
    const bodyObj = sceneObjectsRef?.current?.[target.bodyUuid] || null;
    const frameWorld =
      target.kind === "body"
        ? computeParentWorldMatrix(sceneObj)
        : bodyObj
          ? computeBodyWorldMatrix(bodyObj)
          : computeParentWorldMatrix(sceneObj);
    const parentQuat = new THREE.Quaternion().setFromRotationMatrix(frameWorld);
    const camPos = new THREE.Vector3();
    camera.getWorldPosition(camPos);
    const distance = camPos.distanceTo(origin);
    updateRotationGizmoTransform(gizmo, origin, parentQuat, distance);
    if (!gizmo.root.visible) gizmo.root.visible = true;
  };

  const detach = () => {
    if (dragState) {
      try {
        onPointerUpWindow(new PointerEvent("pointerup"));
      } catch {
        // ignore
      }
    }
    window.removeEventListener("pointerdown", onPointerDownWindow, true);
    rendererDom.removeEventListener("pointermove", onPointerMoveCanvas);
    rendererDom.removeEventListener("pointerleave", onPointerLeaveCanvas);
    if (rendererDom.style.cursor === "grab" || rendererDom.style.cursor === "grabbing") {
      rendererDom.style.cursor = "";
    }
    disposeRotationGizmo(gizmo);
    dispatchDragEvent("rotation-gizmo-drag-end", null);
  };

  return { frameUpdate, detach };
}
